import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface ThemeContextType {
    isDarkMode: boolean;
    toggleDarkMode: () => void;
    primaryColor: string;
    setPrimaryColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
        const storedTheme = localStorage.getItem("theme");
        return storedTheme === "dark";
    });

    const [primaryColor, setPrimaryColor] = useState<string>(() => {
        const storedColor = localStorage.getItem("primaryColor");
        return storedColor || "#9f8f5e"; // Default to primary-500
    });

    useEffect(() => {
        document.body.classList.toggle("dark-mode", isDarkMode);
        localStorage.setItem("theme", isDarkMode ? "dark" : "light");
    }, [isDarkMode]);

    useEffect(() => {
        // Update CSS variables when primary color changes
        const root = document.documentElement;
        root.style.setProperty("--primary-color", primaryColor);

        // Convert hex to R, G, B for rgba() usage
        const r = parseInt(primaryColor.slice(1, 3), 16);
        const g = parseInt(primaryColor.slice(3, 5), 16);
        const b = parseInt(primaryColor.slice(5, 7), 16);
        root.style.setProperty("--primary-color-rgb", `${r}, ${g}, ${b}`);

        root.style.setProperty("--primary-light", adjustColor(primaryColor, 20)); // Lighter version
        root.style.setProperty("--primary-dark", adjustColor(primaryColor, -20)); // Darker version
        localStorage.setItem("primaryColor", primaryColor);
    }, [primaryColor]);

    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
    };

    // Helper function to adjust color brightness
    const adjustColor = (color: string, percent: number): string => {
        const num = parseInt(color.replace("#", ""), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) + amt;
        const G = ((num >> 8) & 0x00ff) + amt;
        const B = (num & 0x0000ff) + amt;
        return (
            "#" +
            (
                0x1000000 +
                (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
                (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
                (B < 255 ? (B < 1 ? 0 : B) : 255)
            )
                .toString(16)
                .slice(1)
        );
    };

    return (
        <ThemeContext.Provider
            value={{ isDarkMode, toggleDarkMode, primaryColor, setPrimaryColor }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};
